import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UserDataService } from '../../../../shared/services/auth/userData.service';
import { Icons } from '../../../../shared/helpers/icons';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { LucideAngularModule } from 'lucide-angular';
import { PopupMessageComponent } from '../../../components/popup-message/popup-message.component';
import { SpinnerComponent } from '../../../components/spinner/spinner.component';
import { CaseClosingSummaryService } from '../../../../shared/services/caseClosingSummary.service';
import { ICaseClosingSummary } from '../../../../shared/models/caseClosingSummary';
import { CaseManagerInfoComponent } from './case-manager-info/case-manager-info.component';
import { CaseDispositionComponent } from './case-disposition/case-disposition.component';
import { CaseClosingSignatureComponent } from './case-closing-signature/case-closing-signature.component';
import { CaseClosingSubmissionComponent } from './case-closing-submission/case-closing-submission.component';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'case-closing-summary',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    LucideAngularModule,
    PopupMessageComponent,
    SpinnerComponent,
    CaseManagerInfoComponent,
    CaseDispositionComponent,
    CaseClosingSignatureComponent,
    CaseClosingSubmissionComponent
  ],
  templateUrl: './case-closing-summary.component.html',
  styleUrl: './case-closing-summary.component.css'
})
export class CaseClosingSummaryComponent implements OnInit {

  icons = Icons;
  currentBuildingId: string | null = null;

  currentCaseId: string | null = null;
  currentCaseClosingSummary: any | null = null;
  stepsConfig = [
    {
      id: 1,
      completionProperty: 'info_section_completion',
      nextStepDependency: null
    },
    {
      id: 2,
      completionProperty: 'case_disposition_section_completion',
      nextStepDependency: 'info_section_completion'
    },
    {
      id: 3,
      completionProperty: 'official_signatures_completion',
      nextStepDependency: 'case_disposition_section_completion'
    },
  ];
  finalStepConfig = {
      id: 4,
      completionProperty: 'case_closing_completion',
      nextStepDependency: 'official_signatures_completion'
  };
  steps = [1, 2, 3];
  currentStep: number = 1;
  tooltips = ['Case Manager Information', 'Threat Assessment Case Disposition', 'Official Signature', 'Submission'];
  messageModal: string | null = null;
  popupMessage: string | null = null;

  loading: boolean = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private userDataservice: UserDataService,
    private caseClosingSummaryService: CaseClosingSummaryService,
    private toastr: ToastrService
  ) {}

  
  ngOnInit(): void {
    this.currentBuildingId = this.userDataservice.getBuildingId();
    
    this.route.paramMap.subscribe((params) => {
      this.currentCaseId = params.get('caseId');
    });
    
    this.loadCaseClosingSummary();
  }

  loadCaseClosingSummary(): void {
    if (this.currentCaseId) {
      this.loading = true;
      this.caseClosingSummaryService.getCaseClosingSummaryByCaseId(this.currentCaseId).subscribe({
        next: (response) => {
          if (Object.keys(response).length === 0) {
            this.currentCaseClosingSummary = null;
            this.loading = false;
            return
          } else {
            this.currentCaseClosingSummary = response;
            this.loading = false;
            return
          }
        },
        error: (error) => {
          this.loading = false;
          console.error('Error:', error);
          this.messageModal = 'unsuccess';
          this.popupMessage = error.message;
        }
      });
    }
  }

  handleUpdateCaseClosingSummary(data: ICaseClosingSummary, section: number): void {
    if (this.currentCaseId && this.currentBuildingId) {
      this.loading = true;
      const fullData = data;
      fullData.case_id = this.currentCaseId!;
      fullData.section = section;
      fullData.building_id = Number(this.currentBuildingId);
      this.caseClosingSummaryService.updateCaseClosingSummary(fullData).subscribe({
        next: (response) => {
          this.loading = false;
          this.loadCaseClosingSummary();
          this.toastr.success('Saved successful!', 'Success');
          this.currentStep = this.currentStep != this.finalStepConfig.id ? section + 1 : this.currentStep;
          if (section == 4 ) {
            this.router.navigate(['/monitoring/']);
          }
        },
        error: (error) => {
          this.loading = false;
          console.error('Error updating case closing summary:', error);
          this.toastr.error(error.message, 'Error');
        }
      });
    }
  }

  onBack(): void {
    this.router.navigate(['/monitoring/']);
  }

  // move to next section
  goToStep(stepId: number): void {
    if (this.canMoveToStep(stepId)) {
      this.currentStep = stepId;
    } else {
      console.error('You must complete the required steps before proceeding.');
    }
  }
  // check if they already completed the required step before allowing them to move to the next step
  canMoveToStep(stepId: number): boolean {
    let stepConfig;
    if (stepId === this.finalStepConfig.id) {
      stepConfig = this.finalStepConfig; // Handle the final step separately
    } else {
      stepConfig = this.stepsConfig.find(step => step.id === stepId);
    }
  
    if (!stepConfig) {
      console.error('Step configuration not found for step', stepId);
      return false;
    }
  
    if (stepConfig.nextStepDependency && this.currentCaseClosingSummary) {
      return this.currentCaseClosingSummary?.[stepConfig.nextStepDependency] === 1;
    }
  
    return true;
  }

  
  resetMessageModal(): void {
    this.messageModal = null;
    this.popupMessage = null;
  }

}
